/* eslint-disable no-restricted-syntax, no-bitwise, no-restricted-properties, prefer-destructuring */
// Above: Lots of for in loops, c-ish syntax and other stuff

import { merge } from "lodash";

import type { IPoint } from "App/Modules/Models";

import numeric from "numeric";

interface IHumanSizeOptions {
    map: IMapOptions;
    camera: ICameraOptions;
}
interface IMapOptions {
    realWidth: number;
    realHeight: number;
    footprintPolygonSides: 4;
    footprint: IPoint[]; // Map pixel coordinates
    scalePoints: IPoint[]; // Map pixel coordinates
    scaleValue: number; // Metric distance betwen the reference points in scalePoints
    cameraPosition: IPoint;
}
interface ICameraOptions {
    realWidth: number;
    realHeight: number;

    // Size of the internal lookup table used to compute the human size. As long as we don't
    // support roll (rotating the camera around the optical axis), the human size will be independent
    // of the x coordinate, so the matrixWidth can be very small. This improves responsiveness.
    matrixWidth: number;
    matrixHeight: number;
    horizonPolygonSides: number;
    horizon: IPoint[]; // In lookup pixel coordinates
}

// this service requires numeric-1.2.6.js (latest, build 2012... :-O  )
export default class HumanSizeService {
    public static defaultOptions: IHumanSizeOptions = {
        map: {
            realWidth: 320, // TOOD: Probably don't default to specific numbers. Can cause
            realHeight: 240, // subtle bugs where stuff only work for some resolutions
            footprintPolygonSides: 4,
            footprint: [],
            scalePoints: [],
            scaleValue: 0,
            cameraPosition: null,
        },
        camera: {
            realWidth: 320, // TOOD: Probably don't default to specific numbers (see above)
            realHeight: 240,
            matrixWidth: 64,
            matrixHeight: 500,
            horizonPolygonSides: 4,
            horizon: [],
        },
    };
    // tslint:enable

    public instanceId = this.generateUUID();

    private readonly options: IHumanSizeOptions;

    // The widthMap and depthMap have dimensions matrixWidth/matrixHeight. These are internal lookup tables and
    // can be much smaller than the camera image, for speed. Can also have a different aspect ratio than the
    // camera image. They cover the entire map coverage. The each entry in widthMap contains a value with the
    // unit "map scale units per camera pixel", i.e. a constant telling us how many "map scale units" that we
    // move on the ground plane if we move one camera pixel to the right in the image. Here, a "map scale unit" is
    // the distance between the two map scale reference points. This is for historical reasons. In a future
    // refactoring, we should probably change this to be in the unit "meters / normalized-cam-coords" or similar.
    private widthMap: number[][] = [];

    private cameraXtoMapX: number[][] = [];

    private cameraYtoMapY: number[][] = [];

    private totalMsCheckingPointsInsidePolygon = 0;

    private totalMsCalculatingWidthmap = 0;

    constructor(initOptions: any) {
        this.options = merge({}, HumanSizeService.defaultOptions, initOptions);
    }

    // Set map camera position, input is in relative coordinates
    public setMapCameraPosition(cameraPosition: IPoint) {
        if (cameraPosition == null) {
            this.options.map.cameraPosition = null;
            return;
        }

        this.options.map.cameraPosition = this.toMapSpecificPoint(cameraPosition);
    }

    // Set map footprint polygon, input is in relative coordinates
    public setMapFootprint(mapFootprint: any) {
        if (mapFootprint == null) {
            this.options.map.footprint = [];
            return;
        }

        this.options.map.footprint = this.toMapSpecific(mapFootprint);
    }

    // Set map scale points, input is in relative coordinates
    public setMapScalePoints(mapScalePoints: any) {
        if (mapScalePoints == null) {
            this.options.map.scalePoints = [];
            return;
        }
        this.options.map.scalePoints = this.toMapSpecific(mapScalePoints);
    }

    // Set camera coverage polygon, input is in relative coordinates
    public setCameraCoveragePolygon(cameraCoveragePolygon: any) {
        // Note: Called "horizon" internally for historical reasons. The public name is more in line
        // with the indended future naming scheme.
        if (cameraCoveragePolygon == null) {
            this.options.camera.horizon = [
                { x: 0, y: 0 },
                { x: this.options.camera.matrixWidth, y: 0 },
                { x: this.options.camera.matrixWidth, y: this.options.camera.matrixHeight },
                { x: 0, y: this.options.camera.matrixHeight },
            ];
        } else {
            this.options.camera.horizon = this.toMatrixSpecific(cameraCoveragePolygon);
        }
    }

    public setMapScaleValue(scaleValue: number) {
        this.options.map.scaleValue = scaleValue;
    }

    public hasNeededData() {
        if (
            this.options !== undefined &&
            this.options.map.footprint.length === this.options.map.footprintPolygonSides &&
            this.options.map.scalePoints.length === 2 &&
            this.options.map.scaleValue !== 0 &&
            this.options.camera.horizon.length === this.options.camera.horizonPolygonSides &&
            this.options.map.cameraPosition !== null
        ) {
            return true;
        }

        return false;
    }

    public isFullyConfigured() {
        return this.options !== undefined && this.widthMap.length === this.options.camera.matrixHeight;
    }

    // Input is relative camera image pixels (range 0..1)
    // Output is "relative camera pixels per meter", i.e., if we move
    // one "relative pixel coordinate unit" to the right, how far do we
    // move on the ground plane.
    public getPixelsPerMeter(relativeX: number, relativeY: number) {
        if (!this.isFullyConfigured()) {
            return 0;
        }

        const adjustedMatrixWidth = this.options.camera.matrixWidth - 1;
        const adjustedMatrixHeight = this.options.camera.matrixHeight - 1;

        const actualX = Math.floor(relativeX * adjustedMatrixWidth);
        const actualY = Math.floor(relativeY * adjustedMatrixHeight);

        const mapScaleUnitsPerCameraPixel = this.getMatrix(this.widthMap, actualY, actualX);

        if (mapScaleUnitsPerCameraPixel !== 0) {
            // Convert from "map scale units per camera pixel" to "camera pixels per meter"
            // (using options.map.scaleValue, which expresses "meters per map scale unit")
            const cameraPixelsPerMeter = 1 / (mapScaleUnitsPerCameraPixel * this.options.map.scaleValue);
            return cameraPixelsPerMeter / this.options.camera.realWidth;
        }
        return 0;
    }

    public calculateWidthMap() {
        const errors: any[] = [];
        const hasNeededData = this.hasNeededData();

        this.widthMap = [];

        if (!hasNeededData) {
            errors.push("Missing coordinates");
            return errors;
        }

        this.totalMsCheckingPointsInsidePolygon = 0;
        this.totalMsCalculatingWidthmap = 0;

        const startTime = new Date().getTime();

        const cameraHorizon: number[] = [];
        const mapFootprint: number[] = [];
        const mapCameraPosition: number[] = [];
        const mapScalePoints: number[] = [];

        for (const horizon of this.options.camera.horizon) {
            cameraHorizon.push(horizon.x);
            cameraHorizon.push(horizon.y);
        }

        for (const footprint of this.options.map.footprint) {
            mapFootprint.push(footprint.x);
            mapFootprint.push(footprint.y);
        }

        mapCameraPosition.push(this.options.map.cameraPosition.x);
        mapCameraPosition.push(this.options.map.cameraPosition.y);

        for (const scalePoint of this.options.map.scalePoints) {
            mapScalePoints.push(scalePoint.x);
            mapScalePoints.push(scalePoint.y);
        }

        const scaleDistanceInPixels = Math.sqrt(
            Math.pow(Math.abs(mapScalePoints[0] - mapScalePoints[2]), 2) +
                Math.pow(Math.abs(mapScalePoints[1] - mapScalePoints[3]), 2),
        );

        const mapScaleUnitsPerMapPixel = 1.0 / scaleDistanceInPixels;

        // Set the maximum value for the width map (definining the minimum image size for humans that are far away).
        // This is set such that it corresponds to a human that is 10 pixels high.
        // (Unit check: 1.8[m] / 10[cpx] / scaleValue[m/msu] = 1.8 / 10 / scaleValue [msu/cpx]
        const maximumWidthMapValue = 1.8 / 10 / this.options.map.scaleValue;

        const mwMap = this.initMatrix(
            this.options.camera.matrixWidth,
            this.options.camera.matrixHeight,
            maximumWidthMapValue,
        );

        const cameraXToMapX = this.initZeroMatrix(this.options.camera.matrixWidth, this.options.camera.matrixHeight);
        const cameraYToMapY = this.initZeroMatrix(this.options.camera.matrixWidth, this.options.camera.matrixHeight);

        const B = this.initZeroMatrix(8, 8);
        let BT = this.initZeroMatrix(8, 8);
        let BTmp = this.initZeroMatrix(8, 8);
        const D = this.initZeroMatrix(1, 8);
        const A = this.initZeroMatrix(3, 3);
        let AT = this.initZeroMatrix(3, 3);
        const C = this.initZeroMatrix(3, 1);
        let l = this.initZeroMatrix(1, 8);

        const xy1 = this.initZeroMatrix(1, 3);
        let Axy1 = this.initZeroMatrix(1, 3);
        let Cxy1 = this.initZeroMatrix(1, 1);
        const Cxy1111 = this.initZeroMatrix(1, 3);
        let t = this.initZeroMatrix(1, 3);

        for (let i = 0; i < mapFootprint.length; i++) {
            this.setMatrix(D, i, 0, mapFootprint[i]);
        }

        for (let i = 0; i < cameraHorizon.length; i += 2) {
            this.setMatrix(B, i, 0, cameraHorizon[i]);
            this.setMatrix(B, i, 1, cameraHorizon[i + 1]);
            this.setMatrix(B, i, 2, 1.0);
            this.setMatrix(B, i, 6, -1.0 * cameraHorizon[i] * mapFootprint[i]);
            this.setMatrix(B, i, 7, -1.0 * cameraHorizon[i + 1] * mapFootprint[i]);

            this.setMatrix(B, i + 1, 3, cameraHorizon[i]);
            this.setMatrix(B, i + 1, 4, cameraHorizon[i + 1]);
            this.setMatrix(B, i + 1, 5, 1.0);
            this.setMatrix(B, i + 1, 6, -1.0 * cameraHorizon[i] * mapFootprint[i + 1]);
            this.setMatrix(B, i + 1, 7, -1.0 * cameraHorizon[i + 1] * mapFootprint[i + 1]);
        }

        BT = numeric.transpose(B);

        BTmp = numeric.dotMMsmall(BT, B);

        BTmp = numeric.inv(BTmp);

        BTmp = numeric.dotMMsmall(BTmp, BT);

        l = numeric.dotMMsmall(BTmp, D);

        for (let i = 0; i < 2; i++) {
            for (let j = 0; j < 3; j++) {
                this.setMatrix(A, j, i, this.getMatrix(l, j + i * 3, 0));
            }
        }

        this.setMatrix(A, 2, 2, 1.0);

        AT = numeric.transpose(A);

        this.setMatrix(C, 0, 0, this.getMatrix(l, 6, 0));
        this.setMatrix(C, 0, 1, this.getMatrix(l, 7, 0));
        this.setMatrix(C, 0, 2, 1.0);

        const cameraPointsX: number[] = [];
        const cameraPointsY: number[] = [];

        for (let i = 0; i < cameraHorizon.length; i += 2) {
            if (cameraHorizon.length < i + 1) {
                break;
            }
            cameraPointsX.push(cameraHorizon[i]);
            cameraPointsY.push(cameraHorizon[i + 1]);
        }

        let isCurrentPointInsideHorizon = 0;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const startMdLoop = new Date().getTime();
        let totalMdLoopDivideTime = 0;

        let hitCount = 0;
        for (let r = 0; r < mwMap.length; r++) {
            // var logRow = "";
            for (let c = 0; c < mwMap[r].length; c++) {
                isCurrentPointInsideHorizon = this.pointInsidePolygon(
                    this.options.camera.horizonPolygonSides,
                    cameraPointsX,
                    cameraPointsY,
                    c,
                    r,
                );

                if (isCurrentPointInsideHorizon !== 0) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    hitCount++;
                    this.setMatrix(xy1, 0, 0, c);
                    this.setMatrix(xy1, 1, 0, r);
                    this.setMatrix(xy1, 2, 0, 1.0);

                    Axy1 = numeric.dotMMsmall(AT, xy1);
                    Cxy1 = numeric.dotMMsmall(C, xy1);

                    this.setMatrix(Cxy1111, 0, 0, this.getMatrix(Cxy1, 0, 0));
                    this.setMatrix(Cxy1111, 1, 0, this.getMatrix(Cxy1, 0, 0));
                    this.setMatrix(Cxy1111, 2, 0, this.getMatrix(Cxy1, 0, 0));

                    const startDivide = new Date().getTime();
                    t = numeric.div(Axy1, Cxy1111);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    totalMdLoopDivideTime += new Date().getTime() - startDivide;

                    const x = this.getMatrix(t, 0, 0);
                    const y = this.getMatrix(t, 1, 0);

                    this.setMatrix(cameraXToMapX, r, c, x);
                    this.setMatrix(cameraYToMapY, r, c, y);
                }
            }
        }

        /* eslint-disable @typescript-eslint/no-unused-vars */
        const stopMdLoop = new Date().getTime();

        const startMwLoop = new Date().getTime();
        /* eslint-enable @typescript-eslint/no-unused-vars */

        for (let yy = 0; yy <= mwMap.length - 1; yy++) {
            let alpha = 0;
            let beta = 0;
            const firstCamPoint = { x: -1, y: -1 };
            const lastCamPoint = { x: -1, y: -1 };

            for (let xx = 0; xx <= mwMap[yy].length - 1; xx++) {
                alpha = this.pointInsidePolygon(
                    this.options.camera.horizonPolygonSides,
                    cameraPointsX,
                    cameraPointsY,
                    xx,
                    yy,
                );
                beta = this.pointInsidePolygon(
                    this.options.camera.horizonPolygonSides,
                    cameraPointsX,
                    cameraPointsY,
                    xx + 1,
                    yy,
                );

                if (alpha === 0 && beta !== 0) {
                    firstCamPoint.x = xx + 1;
                    firstCamPoint.y = yy;
                } else if (alpha !== 0 && beta !== 0 && xx === 0) {
                    firstCamPoint.x = xx;
                    firstCamPoint.y = yy;
                }

                if (alpha !== 0 && beta === 0) {
                    lastCamPoint.x = xx;
                    lastCamPoint.y = yy;
                    break;
                }
            }

            // Todo: Wtf?! (casts added during conversion)
            if (firstCamPoint.x !== -1 && firstCamPoint.y !== -1 && lastCamPoint.x !== -1 && lastCamPoint.y !== -1) {
                const firstMapPoint = {
                    x: this.getMatrix(cameraXToMapX, firstCamPoint.y, firstCamPoint.x),
                    y: this.getMatrix(cameraYToMapY, firstCamPoint.y, firstCamPoint.x),
                };

                const lastMapPoint = {
                    x: this.getMatrix(cameraXToMapX, lastCamPoint.y, lastCamPoint.x),
                    y: this.getMatrix(cameraYToMapY, lastCamPoint.y, lastCamPoint.x),
                };

                const lookupPixelDistance = lastCamPoint.x - firstCamPoint.x + 1;
                const mapPixelDistance = Math.sqrt(
                    Math.pow(Math.abs(lastMapPoint.x - firstMapPoint.x), 2) +
                        Math.pow(Math.abs(lastMapPoint.y - firstMapPoint.y), 2),
                );

                const lookupPixelsPerCameraPixels = this.options.camera.matrixWidth / this.options.camera.realWidth;

                let mapScaleUnitsPerCameraPixel =
                    ((mapPixelDistance * mapScaleUnitsPerMapPixel) / lookupPixelDistance) * lookupPixelsPerCameraPixels;

                if (mapScaleUnitsPerCameraPixel > maximumWidthMapValue) {
                    mapScaleUnitsPerCameraPixel = maximumWidthMapValue;
                }

                for (let x = firstCamPoint.x; x <= lastCamPoint.x; x++) {
                    this.setMatrix(mwMap, yy, x, mapScaleUnitsPerCameraPixel);
                }
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const stopMwLoop = new Date().getTime();

        this.widthMap = mwMap;

        this.totalMsCalculatingWidthmap = new Date().getTime() - startTime;

        return errors;
    }

    private generateUUID(): string {
        let d = new Date().getTime();
        const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === "x" ? r : (r & 0x7) | 0x8).toString(16);
        });
        return uuid;
    }

    private printZerosInsideHorizon(matrix: number[][]) {
        const cameraX: number[] = [];
        const cameraY: number[] = [];
        const result: IPoint[] = [];

        this.options.camera.horizon.forEach((point) => {
            cameraX.push(point.x);
            cameraY.push(point.y);
        });

        for (let r = 0; r < matrix.length; r++) {
            for (let c = 0; c < matrix[r].length; c++) {
                if (this.pointInsidePolygon(this.options.camera.horizonPolygonSides, cameraX, cameraY, c, r)) {
                    const value = this.getMatrix(matrix, r, c);
                    if (value === 0) {
                        result.push({ x: c, y: r });
                    }
                }
            }
        }
    }

    private setMatrix(matrix: number[][], row: number, col: number, value: number) {
        matrix[row][col] = value;
    }

    private getMatrix(matrix: number[][], row: number, col: number): number {
        return matrix[row][col];
    }

    private pointInsidePolygon(nvert: any, vertx: any, verty: any, testx: any, testy: any) {
        const startTime = new Date().getTime();
        let i: number;
        let j: number;
        let c = false;

        for (i = 0, j = nvert - 1; i < nvert; j = i++) {
            if (
                // eslint-disable-next-line
                verty[i] > testy !== verty[j] > testy &&
                // eslint-disable-next-line
                testx < ((vertx[j] - vertx[i]) * (testy - verty[i])) / (verty[j] - verty[i]) + vertx[i]
            ) {
                c = !c;
            }
        }

        this.totalMsCheckingPointsInsidePolygon += new Date().getTime() - startTime;

        // returns 0 if not inside polygon
        // returns -1 if inside polygon
        return c ? -1 : 0;
    }

    private initZeroMatrix(width: number, height: number): number[][] {
        const result: number[][] = [];
        for (let i = 0; i < height; i++) {
            const row: number[] = [];

            for (let j = 0; j < width; j++) {
                row.push(0);
            }

            result.push(row);
        }

        return result;
    }

    private initMatrix(width: number, height: number, value: number): number[][] {
        const result: number[][] = [];
        for (let i = 0; i < height; i++) {
            const row: number[] = [];

            for (let j = 0; j < width; j++) {
                row.push(value);
            }

            result.push(row);
        }

        return result;
    }

    private getCameraToMatrixRelativePoint(point: any) {
        return {
            x: (point.x * this.options.camera.matrixWidth) / this.options.camera.realWidth,
            y: (point.y * this.options.camera.matrixHeight) / this.options.camera.realHeight,
        };
    }

    private toMatrixSpecific(points: IPoint[]): IPoint[] {
        const result: IPoint[] = [];
        for (const point of points) {
            result.push(this.toMatrixSpecificPoint(point));
        }
        return result;
    }

    private toMatrixSpecificPoint(point: IPoint): IPoint {
        return {
            x: Math.floor(point.x * this.options.camera.matrixWidth),
            y: Math.floor(point.y * this.options.camera.matrixHeight),
        };
    }

    private toMatrixRelative(points: IPoint[]): IPoint[] {
        const result = [];
        for (const point of points) {
            result.push(this.toMatrixRelativePoint(point));
        }
        return result;
    }

    private toMatrixRelativePoint(points: IPoint): IPoint {
        return {
            x: (points.x * 1.0) / this.options.camera.matrixWidth,
            y: (points.y * 1.0) / this.options.camera.matrixHeight,
        };
    }

    private toMapSpecific(points: IPoint[]): IPoint[] {
        const result = [];
        for (const point of points) {
            result.push(this.toMapSpecificPoint(point));
        }
        return result;
    }

    private toMapSpecificPoint(point: IPoint): IPoint {
        return {
            x: point.x * this.options.map.realWidth,
            y: point.y * this.options.map.realHeight,
        };
    }

    private toMapRelative(points: IPoint[]): IPoint[] {
        const result = [];
        for (const point of points) {
            result.push(this.toMapRelativePoint(point));
        }
        return result;
    }

    private toMapRelativePoint(point: IPoint): IPoint {
        return {
            x: (point.x * 1.0) / this.options.map.realWidth,
            y: (point.y * 1.0) / this.options.map.realHeight,
        };
    }
}
// eslint-enable
